import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionEtudiants() {
    const [etudiants, setEtudiants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState(null);

    // Utilisation de la configuration centralisée
    const API_BASE = `${config.apiBase}/technicien`;

    const fetchEtudiants = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/get_etudiants.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setEtudiants(data.etudiants);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const bannirEtudiant = async (id, action) => {
        const confirmMsg = action === 'bannir' 
            ? '⚠️ Êtes-vous sûr de vouloir BANNIR cet étudiant ? Il ne pourra plus se connecter.' 
            : '✅ Réactiver cet étudiant ?';
        
        if (!window.confirm(confirmMsg)) return;

        setActionLoading(id);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/bannir_etudiant.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_utilisateur: id, action })
            });
            const data = await response.json();
            if (data.success) {
                toast.success(data.message);
                fetchEtudiants();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setActionLoading(null);
        }
    };

    const supprimerEtudiant = async (id, nom, prenom) => {
        const confirmMsg = `🗑️ Êtes-vous ABSOLUMENT sûr de vouloir supprimer définitivement ${prenom} ${nom} ?\n\n⚠️ Cette action est IRRÉVERSIBLE !\n- Toutes ses données seront supprimées\n- Ses cartes seront supprimées\n- Son historique de transactions sera perdu`;
        
        if (!window.confirm(confirmMsg)) return;
        
        const doubleConfirm = prompt(`Tapez "SUPPRIMER" pour confirmer la suppression définitive de ${prenom} ${nom}:`);
        if (doubleConfirm !== "SUPPRIMER") {
            toast.error('Suppression annulée');
            return;
        }

        setActionLoading(id);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/supprimer_etudiant.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_utilisateur: id })
            });
            const data = await response.json();
            if (data.success) {
                toast.success(`Étudiant ${prenom} ${nom} supprimé définitivement`);
                fetchEtudiants();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        } finally {
            setActionLoading(null);
        }
    };

    useEffect(() => {
        fetchEtudiants();
    }, []);

    const filteredEtudiants = etudiants.filter(etudiant => {
        const matchesSearch = 
            etudiant.nom?.toLowerCase().includes(search.toLowerCase()) ||
            etudiant.prenom?.toLowerCase().includes(search.toLowerCase()) ||
            etudiant.email?.toLowerCase().includes(search.toLowerCase()) ||
            etudiant.telephone?.includes(search);
        
        const matchesFilter = filter === 'all' || etudiant.statut === filter;
        
        return matchesSearch && matchesFilter;
    });

    const getStatutBadge = (statut) => {
        const badges = {
            'actif': 'bg-green-100 text-green-800',
            'banni': 'bg-red-100 text-red-800',
            'en_attente_validation': 'bg-yellow-100 text-yellow-800'
        };
        const labels = {
            'actif': '✅ Actif',
            'banni': '🚫 Banni',
            'en_attente_validation': '⏳ En attente'
        };
        return (
            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${badges[statut] || 'bg-gray-100'}`}>
                {labels[statut] || statut}
            </span>
        );
    };

    const getStatistiques = () => {
        const total = etudiants.length;
        const actifs = etudiants.filter(e => e.statut === 'actif').length;
        const bannis = etudiants.filter(e => e.statut === 'banni').length;
        const enAttente = etudiants.filter(e => e.statut === 'en_attente_validation').length;
        return { total, actifs, bannis, enAttente };
    };

    const stats = getStatistiques();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-7xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-red-600 to-pink-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">👥 Gestion des étudiants</h1>
                    <p className="mt-2 opacity-90">
                        Gérez les comptes étudiants : bannissement, réactivation, suppression définitive
                    </p>
                </div>

                {/* Statistiques */}
                <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">Total</p>
                        <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                    </div>
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">Actifs</p>
                        <p className="text-2xl font-bold text-green-600">{stats.actifs}</p>
                    </div>
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">Bannis</p>
                        <p className="text-2xl font-bold text-red-600">{stats.bannis}</p>
                    </div>
                    <div className="rounded-xl bg-white p-4 shadow-md">
                        <p className="text-sm text-slate-500">En attente</p>
                        <p className="text-2xl font-bold text-yellow-600">{stats.enAttente}</p>
                    </div>
                </div>

                {/* Filtres et recherche */}
                <div className="mb-6 flex flex-wrap gap-4">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="🔍 Rechercher par nom, email, téléphone..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 outline-none focus:border-red-400"
                        />
                    </div>
                    <div className="flex gap-2">
                        {[
                            { value: 'all', label: 'Tous' },
                            { value: 'actif', label: 'Actifs' },
                            { value: 'banni', label: 'Bannis' },
                            { value: 'en_attente_validation', label: 'En attente' }
                        ].map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setFilter(opt.value)}
                                className={`rounded-lg px-4 py-2 transition ${
                                    filter === opt.value
                                        ? 'bg-red-600 text-white'
                                        : 'bg-white text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Liste des étudiants */}
                {loading ? (
                    <div className="flex h-64 items-center justify-center">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-red-200 border-t-red-600"></div>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-xl bg-white shadow-lg">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Étudiant</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Contact</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Institution</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Statut</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 bg-white">
                                    {filteredEtudiants.map((etudiant) => (
                                        <tr key={etudiant.id_utilisateur} className="hover:bg-slate-50">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-medium text-slate-900">
                                                        {etudiant.prenom} {etudiant.nom}
                                                    </p>
                                                    <p className="text-sm text-slate-500">
                                                        ID: {etudiant.id_utilisateur}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-sm">{etudiant.email}</p>
                                                <p className="text-sm text-slate-500">{etudiant.telephone}</p>
                                            </td>
                                            <td className="px-6 py-4 text-sm">{etudiant.institution || '-'}</td>
                                            <td className="px-6 py-4">{getStatutBadge(etudiant.statut)}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    {etudiant.statut === 'banni' ? (
                                                        <button
                                                            onClick={() => bannirEtudiant(etudiant.id_utilisateur, 'reactiver')}
                                                            disabled={actionLoading === etudiant.id_utilisateur}
                                                            className="rounded-lg bg-green-100 px-3 py-1 text-sm text-green-700 transition hover:bg-green-200 disabled:opacity-50"
                                                        >
                                                            {actionLoading === etudiant.id_utilisateur ? '...' : 'Réactiver'}
                                                        </button>
                                                    ) : etudiant.statut === 'actif' && (
                                                        <button
                                                            onClick={() => bannirEtudiant(etudiant.id_utilisateur, 'bannir')}
                                                            disabled={actionLoading === etudiant.id_utilisateur}
                                                            className="rounded-lg bg-orange-100 px-3 py-1 text-sm text-orange-700 transition hover:bg-orange-200 disabled:opacity-50"
                                                        >
                                                            {actionLoading === etudiant.id_utilisateur ? '...' : 'Bannir'}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => supprimerEtudiant(etudiant.id_utilisateur, etudiant.nom, etudiant.prenom)}
                                                        disabled={actionLoading === etudiant.id_utilisateur}
                                                        className="rounded-lg bg-red-100 px-3 py-1 text-sm text-red-700 transition hover:bg-red-200 disabled:opacity-50"
                                                    >
                                                        🗑️ Supprimer
                                                    </button>
                                                </div>
                                             </td>
                                         </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {filteredEtudiants.length === 0 && (
                            <div className="p-8 text-center text-slate-500">
                                Aucun étudiant trouvé
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}